// ========== src/components/Modals/ChildCredentialsModal.jsx ==========

import { useState, useEffect } from 'react';
import * as api from '../../utils/api';

const ChildCredentialsModal = ({ isOpen, onClose, child }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState({ email: false, password: false });
    
    // Naudojame vietinį state slaptažodžiui, kad pakeitus rodytų naują
    const [localPassword, setLocalPassword] = useState(null);
    
    // Slaptažodžio keitimo states
    const [newPassword, setNewPassword] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const [resetError, setResetError] = useState(null);
    const [resetMessage, setResetMessage] = useState(null);

    useEffect(() => {
        if (isOpen && child) {
            setLocalPassword(child.password);
            setNewPassword('');
            setResetError(null);
            setResetMessage(null);
        }
    }, [isOpen, child]);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setResetError(null);
        setResetMessage(null);
        
        if (!newPassword || newPassword.length < 6) {
            setResetError('Slaptažodis turi būti bent 6 simbolių ilgio');
            return;
        }
        
        setIsResetting(true);
        try {
            const result = await api.resetChildPassword(child._id, newPassword);
            if (result.success) {
                setLocalPassword(newPassword);
                setResetMessage(result.message || 'Slaptažodis pakeistas ir išsiųstas jums el. paštu!');
                setNewPassword('');
            } else {
                setResetError(result.detail || 'Įvyko klaida keičiant slaptažodį');
            }
        } catch (err) {
            setResetError(err.message || 'Nepavyko pakeisti slaptažodžio');
        } finally {
            setIsResetting(false);
        }
    };

    if (!isOpen || !child) return null;

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied({ ...copied, [field]: true });
            setTimeout(() => setCopied({ ...copied, [field]: false }), 2000);
        });
    };

    const handlePrint = () => {
        const printWindow = window.open('', '', 'width=600,height=400');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Vaiko Prisijungimo Duomenys</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 40px; }
                        h1 { color: #7c3aed; }
                        .info { margin: 20px 0; padding: 15px; background: #f3f4f6; border-radius: 8px; }
                        .label { font-weight: bold; color: #374151; }
                        .value { font-size: 18px; margin: 5px 0; }
                        .warning { color: #dc2626; margin-top: 20px; padding: 10px; border: 2px solid #dc2626; border-radius: 8px; }
                    </style>
                </head>
                <body>
                    <h1>🎓 Vaiko Prisijungimo Duomenys</h1>
                    <div class="info">
                        <div class="label">Vardas:</div>
                        <div class="value">${child.name}</div>
                    </div>
                    <div class="info">
                        <div class="label">El. paštas (prisijungimui):</div>
                        <div class="value">${child.email}</div>
                    </div>
                    <div class="info">
                        <div class="label">Slaptažodis:</div>
                        <div class="value">${localPassword || '••••••••'}</div>
                    </div>
                    <div class="warning">
                        ⚠️ <strong>Svarbu!</strong> Išsaugokite šiuos duomenis saugioje vietoje. 
                        Vaikas naudos juos prisijungimui į sistemą.
                    </div>
                    <p style="margin-top: 30px; color: #6b7280;">Sukurta: ${new Date().toLocaleString('lt-LT')}</p>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-5 relative overflow-y-auto max-h-[90vh]">
                <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{localPassword ? '✅' : '🔑'}</div>
                    <h2 className="text-xl font-bold text-gray-800 mb-1">
                        {localPassword && !resetMessage ? 'Vaikas sėkmingai pridėtas!' : 'Prisijungimo duomenys'}
                    </h2>
                    <p className="text-gray-600 text-sm">
                        {localPassword ? 'Išsaugokite šiuos prisijungimo duomenis' : 'Naudokite šį el. paštą prisijungimui'}
                    </p>
                </div>

                <div className="space-y-3 mb-4">
                    {/* Name */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-3">
                        <div className="text-xs font-bold text-gray-600 mb-1">Vardas</div>
                        <div className="text-base font-bold text-gray-800">{child.name}</div>
                    </div>

                    {/* Email */}
                    <div className="bg-blue-50 rounded-xl p-3">
                        <div className="flex justify-between items-start mb-2">
                            <div className="text-sm font-bold text-gray-600">
                                El. paštas (prisijungimui)
                            </div>
                            <button
                                onClick={() => copyToClipboard(child.email, 'email')}
                                className="text-blue-600 hover:text-blue-700 font-bold text-sm flex items-center gap-1"
                            >
                                {copied.email ? '✓ Nukopijuota' : '📋 Kopijuoti'}
                            </button>
                        </div>
                        <div className="text-sm font-mono text-gray-800 break-all">
                            {child.email}
                        </div>
                    </div>

                    {/* Password */}
                    <div className="bg-green-50 rounded-xl p-3">
                        <div className="flex justify-between items-start mb-2">
                            <div className="text-sm font-bold text-gray-600">Slaptažodis</div>
                            {localPassword ? (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="text-green-600 hover:text-green-700 font-bold text-sm"
                                    >
                                        {showPassword ? '🙈 Slėpti' : '👁️ Rodyti'}
                                    </button>
                                    <button
                                        onClick={() => copyToClipboard(localPassword, 'password')}
                                        className="text-green-600 hover:text-green-700 font-bold text-sm flex items-center gap-1"
                                    >
                                        {copied.password ? '✓ Nukopijuota' : '📋 Kopijuoti'}
                                    </button>
                                </div>
                            ) : (
                                <span className="text-xs font-bold text-green-700 bg-green-200 px-2 py-1 rounded">Saugoma sistemoje</span>
                            )}
                        </div>
                        <div className="text-sm font-mono text-gray-800">
                            {localPassword ? (showPassword ? localPassword : '••••••••') : 'Slaptažodis nerodomas saugumo sumetimais'}
                        </div>
                    </div>
                </div>

                {/* Password Reset Section (Only for existing children where initial password is lost) */}
                {!child.password && (
                    <div className="bg-purple-50 rounded-xl p-4 mt-2 mb-4 border border-purple-100">
                        <h3 className="text-sm font-bold text-purple-900 mb-1">Pakeisti slaptažodį</h3>
                        <p className="text-xs text-purple-700 mb-3">
                            Sukursite naują vaiko slaptažodį ir sistema jį atsiųs jums į paštą.
                        </p>
                        
                        {resetError && <div className="text-xs text-red-600 mb-2 bg-red-100 p-2 rounded">{resetError}</div>}
                        {resetMessage && <div className="text-xs text-green-700 mb-2 bg-green-100 p-2 rounded">{resetMessage}</div>}
                        
                        <form onSubmit={handleResetPassword} className="flex gap-2">
                            <input
                                type="text"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Naujas slaptažodis (bent 6 simb.)"
                                className="flex-1 px-3 py-2 text-sm border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                                disabled={isResetting}
                            />
                            <button
                                type="submit"
                                disabled={isResetting || !newPassword}
                                className="px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
                            >
                                {isResetting ? 'Keičiama...' : 'Pakeisti'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Warning */}
                {localPassword && !resetMessage && child.password && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-3 mb-3">
                        <div className="flex items-start gap-2">
                            <div className="text-xl">⚠️</div>
                            <div>
                                <div className="font-bold text-red-800 mb-1">Svarbu!</div>
                                <p className="text-sm text-red-700">
                                    Išsaugokite šiuos duomenis saugioje vietoje. Vaikas naudos juos
                                    prisijungimui į sistemą. Šis langas bus rodomas tik vieną kartą.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Instructions */}
                <div className="bg-gray-50 rounded-xl p-3 mb-4">
                    <div className="font-bold text-gray-800 mb-2">📝 Kaip vaikas prisijungs:</div>
                    <ol className="text-sm text-gray-700 space-y-1 ml-4">
                        <li>1. Eiti į prisijungimo puslapį</li>
                        <li>2. Įvesti el. paštą: <span className="font-mono text-xs break-all">{child.email}</span></li>
                        <li>3. Įvesti slaptažodį</li>
                        <li>4. Spausti "Prisijungti"</li>
                    </ol>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={handlePrint}
                        className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition flex items-center justify-center gap-2"
                    >
                        🖨️ Spausdinti
                    </button>
                    <button
                        onClick={onClose}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold hover:shadow-xl transition transform hover:scale-105"
                    >
                        {localPassword ? 'Gerai, supratau' : 'Uždaryti'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChildCredentialsModal;

